/**
 * Validación de la definición de proceso antes de simular (doc. 04 §4.4).
 * Desde el paso 1.2 se admiten retrabajos: una ruta puede volver a una etapa anterior si tiene un
 * máximo de veces por OT. Un ciclo sin límite se rechaza con un mensaje claro.
 */

import { problemasDistribucion } from '../aleatoriedad/distribuciones';
import type { Observacion } from '../dominio/observacion';
import {
  esObligatoriaPara,
  type Condicion,
  type DefinicionDeProceso,
  type Etapa,
} from '../dominio/proceso';
import { esHoraValida } from '../dominio/tiempo';

const obs = (
  regla: string,
  nivel: Observacion['nivel'],
  que: string,
  donde: string,
  porque: string,
  queHacer: string,
): Observacion => ({ regla, nivel, que, donde, porque, queHacer, elementos: [] });

/** ¿La condición puede cumplirse para este tipo? `si`, `no` o `quizas`. */
function evaluarEstatico(c: Condicion, tipo: string): 'si' | 'no' | 'quizas' {
  if ('tipoUnidad' in c) return c.tipoUnidad === tipo ? 'si' : 'no';
  if ('y' in c) {
    const r = c.y.map((h) => evaluarEstatico(h, tipo));
    if (r.includes('no')) return 'no';
    return r.every((x) => x === 'si') ? 'si' : 'quizas';
  }
  if ('o' in c) {
    const r = c.o.map((h) => evaluarEstatico(h, tipo));
    if (r.includes('si')) return 'si';
    return r.every((x) => x === 'no') ? 'no' : 'quizas';
  }
  if ('no' in c) {
    const r = evaluarEstatico(c.no, tipo);
    return r === 'si' ? 'no' : r === 'no' ? 'si' : 'quizas';
  }
  return 'quizas';
}

/**
 * Etapas siguientes posibles de `etapa` para un tipo de unidad. Una ruta con límite nunca corta la
 * búsqueda (al agotarse, se evalúan las siguientes); con `conLimitadas = false` se omite, lo que da
 * los recorridos «finales», sin repetir los retrabajos.
 */
export function siguientesPosibles(
  proceso: DefinicionDeProceso,
  etapaId: string,
  tipo: string,
  conLimitadas = true,
): string[] {
  const r: string[] = [];
  for (const tr of proceso.transiciones.filter((t) => t.de === etapaId)) {
    if (tr.maximo !== undefined && !tr.sino) {
      if (conLimitadas && (!tr.si || evaluarEstatico(tr.si, tipo) !== 'no')) r.push(tr.a);
      continue;
    }
    if (tr.sino) {
      r.push(tr.a);
      break;
    }
    const e = tr.si ? evaluarEstatico(tr.si, tipo) : 'no';
    if (e === 'no') continue;
    r.push(tr.a);
    if (e === 'si') break;
  }
  return r;
}

/**
 * Etapas por las que puede pasar un tipo de unidad, en el orden declarado: las de sus rutas
 * posibles y las esperas que las interrumpen.
 */
export function etapasDelTipo(proceso: DefinicionDeProceso, tipo: string): string[] {
  const vistas = new Set<string>();
  const pila = proceso.etapas.some((e) => e.id === proceso.etapaInicial)
    ? [proceso.etapaInicial]
    : [];
  while (pila.length) {
    const id = pila.pop()!;
    if (vistas.has(id)) continue;
    vistas.add(id);
    const e = proceso.etapas.find((x) => x.id === id);
    if (e?.interrupcion && evaluarEstatico(e.interrupcion.si, tipo) !== 'no')
      pila.push(e.interrupcion.etapa);
    pila.push(...siguientesPosibles(proceso, id, tipo));
  }
  return proceso.etapas.filter((e) => vistas.has(e.id)).map((e) => e.id);
}

export function validarProceso(proceso: DefinicionDeProceso): Observacion[] {
  const r: Observacion[] = [];
  const nombre = (id: string) => proceso.etapas.find((e) => e.id === id)?.nombre ?? id;
  const ids = new Set<string>();
  for (const e of proceso.etapas) {
    if (ids.has(e.id)) {
      r.push(
        obs(
          'proceso-etapa-duplicada',
          'bloquea',
          `Etapa repetida «${e.nombre}»`,
          'Definición del proceso',
          'Dos etapas con el mismo código hacen ambigua la ruta de las OT.',
          'Cambia el código de una de ellas.',
        ),
      );
    }
    ids.add(e.id);
  }
  const etapas = new Map(proceso.etapas.map((e) => [e.id, e]));
  if (!etapas.has(proceso.etapaInicial)) {
    r.push(
      obs(
        'proceso-sin-inicio',
        'bloquea',
        'El proceso no tiene una etapa inicial válida',
        'Definición del proceso',
        'Sin etapa inicial, los autos que llegan no saben qué hacer.',
        'Indica cuál es la primera etapa.',
      ),
    );
    return r;
  }
  const salidas = proceso.etapas.filter((e) => e.tipo === 'salida');
  if (salidas.length !== 1) {
    r.push(
      obs(
        'proceso-salida',
        'bloquea',
        'El proceso debe tener exactamente una etapa de salida',
        'Definición del proceso',
        'Cada auto debe poder salir del taller por un único final del proceso.',
        'Deja una sola etapa de tipo «salida».',
      ),
    );
  }
  for (const tr of proceso.transiciones) {
    if (!etapas.has(tr.de) || !etapas.has(tr.a)) {
      r.push(
        obs(
          'proceso-transicion-inexistente',
          'bloquea',
          `Transición hacia o desde una etapa inexistente (${tr.de} → ${tr.a})`,
          'Definición del proceso',
          'La ruta de las OT quedaría cortada.',
          'Corrige la transición o crea la etapa.',
        ),
      );
    }
  }
  for (const tr of proceso.transiciones) {
    if (tr.maximo === undefined) continue;
    if (tr.sino || !Number.isInteger(tr.maximo) || tr.maximo < 1 || tr.maximo > 20) {
      r.push(
        obs(
          'proceso-limite',
          'bloquea',
          `Máximo de veces inválido en la ruta de «${nombre(tr.de)}» a «${nombre(tr.a)}»`,
          `Etapa «${nombre(tr.de)}»`,
          tr.sino
            ? 'La ruta por defecto («en otro caso») no puede agotarse: siempre debe haber a dónde seguir.'
            : 'El máximo de veces por OT debe ser un número entero entre 1 y 20.',
          tr.sino ? 'Quita el máximo de la ruta por defecto.' : 'Elige un máximo entre 1 y 20.',
        ),
      );
    }
  }
  const interrupciones = new Set(
    proceso.etapas.flatMap((e) => (e.interrupcion ? [e.interrupcion.etapa] : [])),
  );
  for (const e of proceso.etapas) {
    if (e.interrupcion) {
      const int = etapas.get(e.interrupcion.etapa);
      if (!int || int.tipo !== 'espera-externa') {
        r.push(
          obs(
            'proceso-interrupcion',
            'bloquea',
            `La interrupción de «${e.nombre}» no apunta a una espera externa`,
            `Etapa «${e.nombre}»`,
            'La espera intercalada (por ejemplo, de repuesto) debe ser una espera externa definida.',
            'Corrige la etapa de interrupción.',
          ),
        );
      }
      const f = e.interrupcion.fraccion;
      const ok =
        problemasDistribucion(f).length === 0 &&
        (f.tipo === 'constante'
          ? f.valor <= 1
          : f.tipo === 'uniforme' || f.tipo === 'triangular'
            ? f.max <= 1
            : false);
      if (!ok) {
        r.push(
          obs(
            'proceso-interrupcion-fraccion',
            'bloquea',
            `El momento de la interrupción de «${e.nombre}» no es válido`,
            `Etapa «${e.nombre}»`,
            'Debe ser una fracción entre 0 y 1 del trabajo.',
            'Usa valores entre 0 % y 100 %.',
          ),
        );
      }
    }
    if (e.tipo === 'espera-externa' && e.personas.length > 0) {
      r.push(
        obs(
          'proceso-espera-con-personas',
          'bloquea',
          `La espera «${e.nombre}» no puede usar personas`,
          `Etapa «${e.nombre}»`,
          'Una espera externa depende de algo fuera del taller (cliente, proveedor).',
          'Quita las personas o conviértela en actividad.',
        ),
      );
    }
    if (
      e.ventanaHabil &&
      (!esHoraValida(e.ventanaHabil.inicio) ||
        !esHoraValida(e.ventanaHabil.fin) ||
        e.ventanaHabil.inicio >= e.ventanaHabil.fin)
    ) {
      r.push(
        obs(
          'proceso-ventana',
          'bloquea',
          `Horario hábil inválido en «${e.nombre}»`,
          `Etapa «${e.nombre}»`,
          'El reloj hábil necesita un inicio anterior al fin.',
          'Corrige las horas (formato hh:mm).',
        ),
      );
    }
    for (const req of e.personas) {
      if (!req.deLaUnidad) continue;
      const retiene = proceso.etapas.some((x) =>
        x.personas.some((p) => p.rol === req.rol && p.retenerHasta === e.id),
      );
      if (!retiene) {
        r.push(
          obs(
            'proceso-persona-retenida',
            'bloquea',
            `«${e.nombre}» continúa con una persona que ninguna etapa anterior retiene`,
            `Etapa «${e.nombre}»`,
            'La etapa necesita que la persona que hizo el trabajo siga asignada al auto.',
            `Indica en la etapa anterior que la persona se retiene hasta «${e.nombre}».`,
          ),
        );
      }
    }
    if (e.tipo !== 'salida' && !interrupciones.has(e.id)) {
      const salientes = proceso.transiciones.filter((t) => t.de === e.id);
      if (salientes.length === 0) {
        r.push(
          obs(
            'proceso-etapa-sin-salida',
            'bloquea',
            `La etapa «${e.nombre}» no tiene salida`,
            `Etapa «${e.nombre}»`,
            'Los autos que terminen esa etapa no tendrían a dónde ir.',
            'Agrega una transición hacia la etapa siguiente.',
          ),
        );
      } else if (!salientes.some((t) => t.sino)) {
        r.push(
          obs(
            'proceso-sin-sino',
            'bloquea',
            `La etapa «${e.nombre}» no tiene ruta por defecto`,
            `Etapa «${e.nombre}»`,
            'Si ninguna condición se cumple, el auto quedaría sin ruta.',
            'Agrega una transición «en otro caso».',
          ),
        );
      }
    }
  }
  if (r.some((o) => o.nivel === 'bloquea')) return r;

  // Alcanzabilidad y ciclos (recorrido por todas las transiciones).
  const alcanzables = new Set<string>([proceso.etapaInicial]);
  const pila = [proceso.etapaInicial];
  while (pila.length) {
    const id = pila.pop()!;
    for (const t of proceso.transiciones.filter((x) => x.de === id)) {
      if (!alcanzables.has(t.a)) {
        alcanzables.add(t.a);
        pila.push(t.a);
      }
    }
  }
  for (const e of proceso.etapas) {
    if (!alcanzables.has(e.id) && !interrupciones.has(e.id)) {
      r.push(
        obs(
          'proceso-inalcanzable',
          'bloquea',
          `La etapa «${e.nombre}» nunca se alcanza`,
          `Etapa «${e.nombre}»`,
          'Ninguna ruta del proceso pasa por ella.',
          'Agrega una transición hacia ella o elimínala.',
        ),
      );
    }
  }
  // Ciclos sin límite: solo cuentan las rutas sin máximo de veces (un retrabajo con límite termina).
  const estado = new Map<string, 0 | 1 | 2>();
  let ciclo: string | null = null;
  let desde: string | null = null;
  const visitar = (id: string) => {
    if (ciclo) return;
    estado.set(id, 1);
    for (const t of proceso.transiciones.filter((x) => x.de === id && x.maximo === undefined)) {
      const s = estado.get(t.a);
      if (s === 1) {
        ciclo = t.a;
        desde = id;
        return;
      }
      if (s === undefined) visitar(t.a);
    }
    estado.set(id, 2);
  };
  for (const e of proceso.etapas) if (!estado.has(e.id)) visitar(e.id);
  if (ciclo) {
    r.push(
      obs(
        'proceso-ciclo',
        'bloquea',
        `El proceso puede volver a «${nombre(ciclo)}» sin límite (retrabajo)`,
        `Ruta de «${nombre(desde ?? ciclo)}» a «${nombre(ciclo)}»`,
        'Un auto podría repetir esas etapas sin fin y nunca salir del taller.',
        'Indica en esa ruta cuántas veces como máximo puede repetirse por OT, o quítala.',
      ),
    );
    return r;
  }

  // Por tipo de unidad: rutas posibles, duraciones y etapas obligatorias que alguna ruta salta.
  for (const tipo of proceso.tiposDeUnidad) {
    const rutas: string[][] = [];
    const recorrer = (id: string, camino: string[]) => {
      if (rutas.length > 500) return;
      const actual = [...camino, id];
      const e = etapas.get(id)!;
      if (e.tipo === 'salida') {
        rutas.push(actual);
        return;
      }
      // Recorridos finales: los retrabajos con límite se agotan y el auto sigue por las demás rutas.
      for (const s of siguientesPosibles(proceso, id, tipo.id, false)) recorrer(s, actual);
    };
    recorrer(proceso.etapaInicial, []);
    const visitadas = new Set(etapasDelTipo(proceso, tipo.id));
    for (const e of proceso.etapas) {
      if (!esObligatoriaPara(e, tipo.id)) continue;
      const salta = rutas.find((ruta) => !ruta.includes(e.id));
      if (salta) {
        r.push(
          obs(
            'proceso-salta-obligatoria',
            'bloquea',
            `Una ruta de «${tipo.nombre}» salta la etapa obligatoria «${e.nombre}»`,
            `Ruta: ${salta.map(nombre).join(' → ')}`,
            'Saltarse una etapa obligatoria es un error del proceso (prompt §6.1).',
            'Corrige las transiciones para que toda ruta pase por la etapa, o márcala como opcional.',
          ),
        );
      }
    }
    const conInterrupcion = [...visitadas].flatMap((id) => {
      const e = etapas.get(id)!;
      return e.interrupcion ? [e.interrupcion.etapa] : [];
    });
    for (const id of [...visitadas, ...conInterrupcion]) {
      const e = etapas.get(id)!;
      if (e.tipo === 'salida') continue;
      r.push(...validarDuracion(e, tipo.id, tipo.nombre));
    }
    const forma = tipo.promesaTipo ?? 'habiles';
    if (forma !== 'habiles' && !(tipo.promesaHora && esHoraValida(tipo.promesaHora.valor))) {
      r.push(
        obs(
          'proceso-promesa-hora',
          'bloquea',
          `Hora de promesa inválida para «${tipo.nombre}»`,
          'Parámetros · Promesas',
          'La promesa a una hora fija necesita la hora (hh:mm).',
          'Escribe la hora de entrega prometida, por ejemplo 17:00.',
        ),
      );
    }
    if (forma === 'habiles' && !(tipo.promesaMin.valor > 0)) {
      r.push(
        obs(
          'proceso-promesa',
          'bloquea',
          `Plazo de promesa inválido para «${tipo.nombre}»`,
          'Parámetros · Promesas',
          'El cumplimiento se mide contra la hora prometida.',
          'Ingresa un plazo mayor que cero.',
        ),
      );
    }
    if (!(tipo.horasVendidas.valor >= 0)) {
      r.push(
        obs(
          'proceso-horas-vendidas',
          'bloquea',
          `Horas vendidas inválidas para «${tipo.nombre}»`,
          'Parámetros · Horas vendidas',
          'La productividad técnica las usa.',
          'Ingresa un valor mayor o igual a cero.',
        ),
      );
    }
  }
  for (const a of proceso.atributos) {
    for (const [tipo, p] of Object.entries(a.probabilidad.valor)) {
      if (!(p >= 0 && p <= 1)) {
        r.push(
          obs(
            'proceso-probabilidad',
            'bloquea',
            `Probabilidad inválida: «${a.nombre}» para ${tipo}`,
            'Parámetros · Probabilidades',
            'Una probabilidad debe estar entre 0 % y 100 %.',
            'Corrige el valor.',
          ),
        );
      }
    }
  }
  const hitos = new Set(proceso.etapas.flatMap((e) => [e.hitoAlIniciar, e.hitoAlTerminar]));
  if (!hitos.has('apertura-ot') || !hitos.has('listo')) {
    r.push(
      obs(
        'proceso-hitos',
        'advierte',
        'Faltan los hitos «apertura de OT» o «listo»',
        'Definición del proceso',
        'Sin ellos no se calculan la promesa ni el lead time a listo.',
        'Marca qué etapa abre la OT y cuál deja el auto listo.',
      ),
    );
  }
  return r;
}

function validarDuracion(e: Etapa, tipo: string, nombreTipo: string): Observacion[] {
  const d = e.duracion?.porTipo[tipo] ?? e.duracion?.porTipo['*'];
  if (!d) {
    return [
      obs(
        'proceso-sin-duracion',
        'bloquea',
        `«${e.nombre}» no tiene duración para «${nombreTipo}»`,
        `Etapa «${e.nombre}»`,
        'El motor no puede simular una etapa sin tiempo.',
        'Ingresa los tiempos mínimo, típico y máximo.',
      ),
    ];
  }
  return problemasDistribucion(d).map((p) =>
    obs(
      'proceso-duracion',
      'bloquea',
      `Tiempos incoherentes en «${e.nombre}»`,
      `Etapa «${e.nombre}» · ${nombreTipo}`,
      p,
      'Corrige los tiempos.',
    ),
  );
}

/**
 * Tipos de lugar efectivos de cada etapa: `actual` hereda los lugares de las etapas previas.
 * Sirve para comprobar que el layout conecta los lugares que el proceso recorre.
 */
export function lugaresEfectivos(proceso: DefinicionDeProceso): Map<string, Set<string>> {
  const lugares = new Map<string, Set<string>>(
    proceso.etapas.map((e) => [e.id, new Set<string>()]),
  );
  const propio = (e: Etapa): string | null =>
    e.tipo === 'salida' ? 'salida' : e.lugar === 'actual' ? null : e.lugar;
  const inicial = proceso.etapas.find((e) => e.id === proceso.etapaInicial);
  if (inicial) lugares.get(inicial.id)!.add(propio(inicial) ?? 'ingreso');
  for (const e of proceso.etapas) {
    const l = propio(e);
    if (l) lugares.get(e.id)!.add(l);
  }
  let cambio = true;
  while (cambio) {
    cambio = false;
    for (const t of proceso.transiciones) {
      const destino = proceso.etapas.find((e) => e.id === t.a);
      if (!destino || propio(destino)) continue;
      const de = lugares.get(t.de);
      const a = lugares.get(t.a);
      if (!de || !a) continue;
      for (const l of de) {
        if (!a.has(l)) {
          a.add(l);
          cambio = true;
        }
      }
    }
  }
  return lugares;
}
